import { useState, useEffect, useCallback } from 'react';
import { Plus, PlusCircle, Trash2, X, Loader2, RotateCcw, Power, Pencil, ChevronRight } from 'lucide-react';
import { api } from '../../lib/spizirnaUi';
import ComboSkupinaEditor from './ComboSkupinaEditor';

const BARVA_PRIDAVKY = '#EC4899';
const BARVA_COMBO = '#22C55E';
const VLASTNI = '#3B82F6';

/** Režim úprav skupiny. Prázdná hodnota = rozsah podle nastavení skupiny. */
const REZIMY_UPRAV = [
    { id: '', label: 'Úpravy dle skupiny' },
    { id: 'volne', label: 'Volně: odebrat i přidat' },
    { id: 'jen_odebrat', label: 'Jen odebrat' },
    { id: 'jen_pridat', label: 'Jen přidat' },
    { id: 'bez_uprav', label: 'Bez úprav' },
];

export const POPIS_POLE = {
    prirazena: 'přiřazení', povinny: 'povinná', rezim_uprav: 'režim úprav', poradi: 'pořadí',
    prodejna: 'prodejná', vychozi_vybrana: 'výchozí', min_ks: 'lze odebrat', max_ks: 'max ks',
    vychozi: 'předvybraná', v_cene: 'v ceně',
};

export const popisHodnoty = (pole, h) => {
    if (h === null || h === undefined) return 'dědí';
    if (pole === 'rezim_uprav') return REZIMY_UPRAV.find(r => r.id === h)?.label || h;
    if (pole === 'min_ks') return h < 0 ? 'ano' : 'ne';
    if (typeof h === 'boolean') return h ? 'ano' : 'ne';
    return String(h);
};

/** Rozsah množství přídavku lidsky — „−1 až 3" na kartě nikomu nic neřekne. */
const popisRozsahu = (r) => {
    if (!r) return 'v pokladně se nenabízí';
    const { min_ks: min, max_ks: max } = r;
    if (min < 0 && max === 0) return 'jen odebrat';
    if (min < 0 && max > 1) return `lze odebrat, až ${max}×`;
    if (min < 0) return 'lze odebrat';
    if (max > 1) return `až ${max}×`;
    return 'jen zaškrtnout';
};

/**
 * Skupiny ingrediencí na kategorii nebo produktu — dědí se po stromu.
 *
 * Kategorie nastaví skupinu pro všechny podkategorie a produkty pod sebou,
 * bližší úroveň vyhrává. U každé hodnoty je vidět, odkud pochází: šedý
 * štítek „z Pizza" = zděděná, modrý „vlastní" = přebitá tady (jde vrátit).
 *
 * Když změna na kategorii narazí pod ní na vlastní jinou hodnotu, server
 * vrátí 409 a tady se ukáže dialog: přepsat zaškrtnutá místa, nebo zachovat.
 *
 * `druh="combo"` (2026-10-22) — totéž pro combo skupiny: dědí se, kde se
 * combo nabízí a které jeho položky; cena comba zůstává ve skupině.
 */
export default function NastaveniPridavku({ typ, id, isDark, obnovit = 0, onZmena, onOtevritUroven, druh = 'pridavky' }) {
    const combo = druh === 'combo';
    const BARVA = combo ? BARVA_COMBO : BARVA_PRIDAVKY;
    const [data, setData] = useState(null);
    const [nacitam, setNacitam] = useState(true);
    const [chyba, setChyba] = useState('');
    const [pridatOtevreno, setPridatOtevreno] = useState(false);
    const [konflikt, setKonflikt] = useState(null); // { zmeny, konflikty, vybrana: Set }
    // Rozbalené skupiny — výchozí je vše sbalené, ať je vidět přehled
    // a otevře se jen to, co člověk zrovna řeší. Pamatuje si to prohlížeč.
    const klicRozbaleni = `nastaveni.${druh}.rozbalene`;
    const [rozbalene, setRozbalene] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem(klicRozbaleni) || '[]')); } catch { return new Set(); }
    });
    const nastavRozbalene = (nove) => {
        setRozbalene(nove);
        try { localStorage.setItem(klicRozbaleni, JSON.stringify([...nove])); } catch { /* jen pohodlí */ }
    };
    const prepnout = (sid) => {
        const nove = new Set(rozbalene);
        if (nove.has(sid)) nove.delete(sid); else nove.add(sid);
        nastavRozbalene(nove);
    };
    // Editor combo skupiny: undefined = zavřený, null = nová, id = úprava.
    const [editorCombo, setEditorCombo] = useState(undefined);

    const muted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const karta = isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)';
    const pole = isDark ? 'rgba(255,255,255,0.06)' : '#fff';

    const url = `/api/recepty/${combo ? 'combo-nastaveni' : 'pridavky-nastaveni'}/${typ}/${id}`;

    const nacist = useCallback(async (tise = false) => {
        if (!tise) setNacitam(true);
        try { setData(await api(url)); setChyba(''); }
        catch (e) { setChyba(e?.error || e?.message || 'Nastavení se nepodařilo načíst'); }
        if (!tise) setNacitam(false);
    }, [url]);

    useEffect(() => { nacist(); }, [nacist]);
    useEffect(() => { if (obnovit) nacist(true); }, [obnovit]); // eslint-disable-line react-hooks/exhaustive-deps

    const ulozit = async (zmeny, rezim = null, prepsat = []) => {
        try {
            setData(await api(url, 'PUT', { zmeny, ...(rezim ? { rezim, prepsat } : {}) }));
            setChyba('');
            setKonflikt(null);
            onZmena?.();
        } catch (e) {
            if (e?.konflikty) {
                setKonflikt({ zmeny, konflikty: e.konflikty, vybrana: new Set(e.konflikty.map(m => `${m.typ}:${m.id}`)) });
            } else {
                setChyba(e?.error || e?.message || 'Změnu se nepodařilo uložit');
                nacist(true);
            }
        }
    };

    const jeTady = (puvod) => !!puvod && puvod.typ === typ && puvod.id === Number(id);

    /** Štítek původu hodnoty; u vlastní hodnoty tlačítko zpět na zděděnou. */
    const Puvod = ({ puvod, onVratit }) => {
        if (!puvod) return null;
        if (jeTady(puvod)) {
            return (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold"
                    style={{ background: `${VLASTNI}1f`, color: VLASTNI }}>
                    vlastní
                    {onVratit && (
                        <button type="button" onClick={onVratit} title="Vrátit na zděděné"
                            style={{ background: 'none', border: 'none', padding: 0, marginLeft: 2, cursor: 'pointer', color: VLASTNI, display: 'inline-flex' }}>
                            <RotateCcw size={10} />
                        </button>
                    )}
                </span>
            );
        }
        const styl = { background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', color: muted };
        // Štítek původu vede tam, kde se hodnota nastavuje.
        if (onOtevritUroven) {
            return (
                <button type="button" onClick={() => onOtevritUroven(puvod)} title={`Otevřít ${puvod.nazev}`}
                    className="px-1.5 py-0.5 rounded text-[9px] font-medium"
                    style={{ ...styl, border: 'none', cursor: 'pointer', textDecoration: 'underline dotted' }}>
                    z {puvod.nazev}
                </button>
            );
        }
        return (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={styl}>
                z {puvod.nazev}
            </span>
        );
    };

    if (nacitam && !data) {
        return <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: muted }} /></div>;
    }

    const skupiny = data?.skupiny || [];
    const nadrazena = (data?.retez || []).slice(0, -1).map(u => u.nazev).join(' › ');

    return (
        <div className="space-y-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>{combo ? 'Combo skupiny' : 'Skupiny ingrediencí'}</h3>
                    <p className="text-[11px] mt-0.5" style={{ color: muted }}>
                        {typ === 'kategorie'
                            ? 'Platí pro všechny podkategorie a produkty pod touto kategorií. Kde má někdo vlastní hodnotu, OS se zeptá, jestli ji přepsat.'
                            : `Hodnoty se dědí${nadrazena ? ` z kategorie ${nadrazena}` : ''}. Změna tady platí jen pro tuto velikost.`}
                    </p>
                </div>
                <button type="button" onClick={() => setPridatOtevreno(true)}
                    className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0"
                    style={{ background: BARVA, color: '#fff', border: 'none', cursor: 'pointer' }}>
                    <Plus size={13} /> {combo ? 'Přidat combo' : 'Přidat skupinu'}
                </button>
            </div>

            {chyba && <p className="text-[11px]" style={{ color: '#EF4444' }}>{chyba}</p>}

            {skupiny.length === 0 && (
                <p className="text-xs py-4 text-center" style={{ color: muted }}>
                    {combo ? 'Žádné combo' : 'Žádné skupiny ingrediencí'}{typ === 'produkt' ? ' — ani zděděné z kategorie' : ''}.
                </p>
            )}

            {skupiny.length > 1 && (
                <div className="flex justify-end">
                    {(() => {
                        const vse = skupiny.every(s => rozbalene.has(s.skupina_id));
                        return (
                            <button type="button"
                                onClick={() => nastavRozbalene(vse ? new Set() : new Set([...rozbalene, ...skupiny.map(s => s.skupina_id)]))}
                                className="text-[11px] font-medium"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, padding: 0 }}>
                                {vse ? 'Sbalit vše' : 'Rozbalit vše'}
                            </button>
                        );
                    })()}
                </div>
            )}

            {skupiny.map(s => {
                const z = (pole, hodnota, pridavekId = null) => ({ skupina_id: s.skupina_id, pridavek_id: pridavekId, pole, hodnota });
                const otevrena = rozbalene.has(s.skupina_id);
                const prodejnych = s.polozky.filter(p => p.prodejna).length;
                const vlastniPrirazeni = jeTady(s.puvod.prirazena);
                const jednoZ = s.typ_vyberu === 'single';
                return (
                    <div key={s.skupina_id} className="rounded-xl overflow-hidden"
                        style={{ background: karta, border: `1px solid ${border}`, opacity: s.prirazena ? 1 : 0.6 }}>
                        <div className="flex items-center justify-between gap-2 px-4 py-2.5 flex-wrap" style={{ borderBottom: otevrena ? `1px solid ${border}` : 'none' }}>
                            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                                <button type="button" onClick={() => prepnout(s.skupina_id)} aria-expanded={otevrena}
                                    title={otevrena ? 'Sbalit' : 'Rozbalit položky'}
                                    className="flex items-center gap-1.5 text-left"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                    <ChevronRight size={15} style={{ color: muted, flexShrink: 0, transform: otevrena ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{s.nazev}</span>
                                    <span className="text-[10px]" style={{ color: muted }}>
                                        {prodejnych === s.polozky.length ? `${s.polozky.length} položek` : `${prodejnych} z ${s.polozky.length} prodejných`}
                                    </span>
                                </button>
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: `${BARVA}1f`, color: BARVA }}>
                                    {combo
                                        ? (s.typ_zobrazeni === 'auto' ? 'auto' : 'nabídka')
                                        : jednoZ ? 'jedna z' : `více${s.max_vyberu ? ` ≤ ${s.max_vyberu}` : ''}`}
                                </span>
                                {!s.aktivni && <span className="text-[10px]" style={{ color: '#F59E0B' }}>skupina je v číselníku vypnutá</span>}
                                {s.prirazena
                                    ? <Puvod puvod={s.puvod.prirazena} />
                                    : <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>vypnutá tady</span>}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {s.prirazena && !combo && (
                                    <>
                                        <span className="flex items-center gap-1">
                                            <button type="button" onClick={() => ulozit([z('povinny', !s.povinny)])}
                                                title="Musí si host ze skupiny vybrat?"
                                                className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                                style={{ cursor: 'pointer', border: `1px solid ${s.povinny ? '#EF444466' : border}`, background: s.povinny ? 'rgba(239,68,68,0.12)' : 'transparent', color: s.povinny ? '#EF4444' : muted }}>
                                                {s.povinny ? 'povinná' : 'volitelná'}
                                            </button>
                                            <Puvod puvod={s.puvod.povinny} onVratit={() => ulozit([z('povinny', null)])} />
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <select value={s.rezim_uprav || ''}
                                                onChange={e => ulozit([z('rezim_uprav', e.target.value || null)])}
                                                title="Co s ingrediencemi skupiny smí obsluha dělat"
                                                className="text-[11px] rounded-md"
                                                style={{ background: pole, border: `1px solid ${border}`, color: 'var(--text-primary)', padding: '3px 6px' }}>
                                                {REZIMY_UPRAV.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                                            </select>
                                            <Puvod puvod={s.puvod.rezim_uprav} onVratit={() => ulozit([z('rezim_uprav', null)])} />
                                        </span>
                                        {/* Kolik kusů ze skupiny je v ceně (2026-10-23). */}
                                        <span className="flex items-center gap-1">
                                            <label className="flex items-center gap-1 text-[10px]" style={{ color: muted }} title="Kolik kusů ze skupiny je v ceně produktu (0 = žádný)">
                                                v ceně
                                                <input type="text" inputMode="numeric" defaultValue={s.v_cene ?? 0} key={`${s.skupina_id}-vc-${s.v_cene}`}
                                                    onFocus={e => e.target.select()}
                                                    onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
                                                    onBlur={e => {
                                                        const t = e.target.value.trim();
                                                        const v = t === '' ? null : Math.min(20, Math.max(0, parseInt(t, 10) || 0));
                                                        if (v !== (s.v_cene ?? 0)) ulozit([z('v_cene', v)]);
                                                    }}
                                                    className="rounded text-center"
                                                    style={{ width: 34, padding: '1px 4px', fontSize: 11, fontWeight: 700, background: pole, border: `1px solid ${s.v_cene > 0 ? '#22C55E88' : border}`, color: 'var(--text-primary)' }} />
                                            </label>
                                            <Puvod puvod={s.puvod.v_cene} onVratit={() => ulozit([z('v_cene', null)])} />
                                        </span>
                                    </>
                                )}
                                {s.prirazena && combo && (
                                    // „V ceně" (2026-10-23): kolik kusů je v ceně jídla, kolik se musí
                                    // a smí vybrat. Nejvýš 0 = bez omezení, nenastavené = 1 kus.
                                    <span className="flex items-center gap-2 flex-wrap">
                                        {[
                                            { pole: 'v_cene', popis: 'v ceně', hodnota: s.v_cene, title: 'Kolik kusů ze skupiny je v ceně jídla (0 = žádný)' },
                                            { pole: 'min_ks', popis: 'aspoň', hodnota: s.min_ks, title: 'Kolik kusů se musí vybrat (kyblík 2)' },
                                            { pole: 'max_ks', popis: 'nejvýš', hodnota: s.max_ks ?? 0, title: 'Kolik kusů celkem i s placenými (0 = bez omezení)' },
                                        ].map(c => (
                                            <span key={c.pole} className="flex items-center gap-1">
                                                <label className="flex items-center gap-1 text-[10px]" style={{ color: muted }} title={c.title}>
                                                    {c.popis}
                                                    <input type="text" inputMode="numeric" defaultValue={c.hodnota} key={`${s.skupina_id}-${c.pole}-${c.hodnota}`}
                                                        onFocus={e => e.target.select()}
                                                        onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
                                                        onBlur={e => {
                                                            const t = e.target.value.trim();
                                                            const v = t === '' ? null : Math.min(20, Math.max(0, parseInt(t, 10) || 0));
                                                            if (v !== c.hodnota) ulozit([z(c.pole, v)]);
                                                        }}
                                                        className="rounded text-center"
                                                        style={{ width: 34, padding: '1px 4px', fontSize: 11, fontWeight: 700, background: pole, border: `1px solid ${c.pole === 'v_cene' && c.hodnota > 0 ? '#22C55E88' : border}`, color: 'var(--text-primary)' }} />
                                                    {c.pole === 'max_ks' && !s.max_ks ? <span>∞</span> : null}
                                                </label>
                                                <Puvod puvod={s.puvod?.[c.pole]} onVratit={() => ulozit([z(c.pole, null)])} />
                                            </span>
                                        ))}
                                    </span>
                                )}
                                {combo && (
                                    <button type="button" onClick={() => setEditorCombo(s.skupina_id)}
                                        title="Upravit položky a ceny comba — platí všude, kde je přiřazené"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: BARVA, padding: 2 }}>
                                        <Pencil size={14} />
                                    </button>
                                )}
                                {s.prirazena ? (
                                    <button type="button"
                                        onClick={() => {
                                            const otazka = vlastniPrirazeni
                                                ? `Odebrat skupinu ${s.nazev}?`
                                                : `Vypnout zděděnou skupinu ${s.nazev} jen tady?`;
                                            if (confirm(otazka)) ulozit([z('prirazena', false)]);
                                        }}
                                        title={vlastniPrirazeni ? 'Odebrat skupinu' : 'Vypnout zděděnou skupinu tady'}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 2 }}>
                                        {vlastniPrirazeni ? <Trash2 size={14} /> : <Power size={14} />}
                                    </button>
                                ) : (
                                    <button type="button" onClick={() => ulozit([z('prirazena', true)])}
                                        className="text-[11px] font-semibold px-2 py-1 rounded-md"
                                        style={{ background: 'none', border: `1px solid ${border}`, color: 'var(--text-primary)', cursor: 'pointer' }}>
                                        Zapnout
                                    </button>
                                )}
                            </div>
                        </div>

                        {otevrena && (
                        <div className="px-3 py-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 6 }}>
                            {s.polozky.map(p => {
                                const vlastniPole = Object.entries(p.puvod).filter(([, pv]) => jeTady(pv)).map(([k]) => k);
                                const zdedenaPole = Object.entries(p.puvod).filter(([, pv]) => pv && !jeTady(pv));
                                const maxKs = p.max_ks ?? s.vychozi_max_ks;
                                const lzeOdebrat = (p.min_ks ?? s.vychozi_min_ks) < 0;
                                return (
                                    <div key={p.id} className="flex flex-col gap-1.5 px-3 py-2 rounded-lg"
                                        style={{
                                            border: `1px solid ${vlastniPole.length ? `${VLASTNI}55` : border}`,
                                            background: vlastniPole.length ? `${VLASTNI}0d` : 'transparent',
                                        }}>
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-medium truncate" style={{
                                                    color: p.prodejna ? 'var(--text-primary)' : muted,
                                                    textDecoration: p.prodejna ? 'none' : 'line-through',
                                                }}>
                                                    {p.nazev}
                                                    {p.ve_slozeni && <span className="ml-1.5 px-1 rounded text-[9px] font-semibold" style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B', textDecoration: 'none', display: 'inline-block' }}>ve složení</span>}
                                                    {!p.clen && <span className="ml-1.5 text-[9px]" style={{ color: muted }}>mimo skupinu</span>}
                                                </div>
                                                <div className="text-[10px]" style={{ color: muted }}>
                                                    {combo
                                                        ? <>v combu <b style={{ color: BARVA_COMBO }}>{Math.round(p.cena_combo)} Kč</b>{p.cena ? ` · běžně ${Math.round(p.cena)} Kč` : ''}</>
                                                        : p.cena ? `+${Math.round(p.cena)} Kč` : 'zdarma'}
                                                    {'rozsah' in p && <span className="ml-1">· {popisRozsahu(p.rozsah)}</span>}
                                                </div>
                                            </div>
                                            <label className="flex items-center gap-1.5 text-[10px] flex-shrink-0" style={{ color: muted, cursor: 'pointer' }}>
                                                prodejné
                                                <input type="checkbox" checked={p.prodejna}
                                                    onChange={e => ulozit([z('prodejna', e.target.checked, p.id)])}
                                                    style={{ accentColor: '#22C55E', width: 15, height: 15, cursor: 'pointer' }} />
                                            </label>
                                        </div>

                                        {p.prodejna && combo && (
                                            <div className="flex items-center gap-1 flex-wrap">
                                                <button type="button" onClick={() => ulozit([z('vychozi', !p.vychozi, p.id)])}
                                                    title="Pokladna ji po ťuknutí na jídlo přidá sama (fries → omáčka)"
                                                    className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                                    style={{ cursor: 'pointer', border: `1px solid ${p.vychozi ? `${BARVA}66` : border}`, background: p.vychozi ? `${BARVA}1f` : 'transparent', color: p.vychozi ? BARVA : muted }}>
                                                    předvybraná
                                                </button>
                                            </div>
                                        )}

                                        {p.prodejna && !combo && (
                                            <div className="flex items-center gap-1 flex-wrap">
                                                <button type="button" onClick={() => ulozit([z('vychozi_vybrana', !p.vychozi_vybrana, p.id)])}
                                                    title="Předvybrat v pokladně"
                                                    className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                                    style={{ cursor: 'pointer', border: `1px solid ${p.vychozi_vybrana ? `${BARVA}66` : border}`, background: p.vychozi_vybrana ? `${BARVA}1f` : 'transparent', color: p.vychozi_vybrana ? BARVA : muted }}>
                                                    výchozí
                                                </button>
                                                {!jednoZ && (
                                                    <>
                                                        <button type="button" onClick={() => ulozit([z('min_ks', lzeOdebrat ? 0 : -1, p.id)])}
                                                            title="Host smí ingredienci odebrat (bez cibule)"
                                                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                                            style={{ cursor: 'pointer', border: `1px solid ${lzeOdebrat ? '#EF444466' : border}`, background: lzeOdebrat ? 'rgba(239,68,68,0.12)' : 'transparent', color: lzeOdebrat ? '#EF4444' : muted }}>
                                                            lze odebrat
                                                        </button>
                                                        <label className="flex items-center gap-1 text-[10px]" style={{ color: muted }}>
                                                            max
                                                            <input type="number" min="0" max="99" defaultValue={maxKs} key={`${p.id}-${maxKs}`}
                                                                onBlur={e => {
                                                                    const v = Math.min(99, Math.max(0, parseInt(e.target.value, 10) || 0));
                                                                    if (v !== maxKs) ulozit([z('max_ks', v, p.id)]);
                                                                }}
                                                                className="rounded"
                                                                style={{ width: 44, padding: '1px 4px', fontSize: 10, background: pole, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                                                            ks
                                                        </label>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {(vlastniPole.length > 0 || zdedenaPole.length > 0) && (
                                            <div className="flex items-center gap-1 flex-wrap text-[9px]" style={{ color: muted }}>
                                                {zdedenaPole.length > 0 && (
                                                    <span>{zdedenaPole.map(([k]) => POPIS_POLE[k]).join(', ')} <Puvod puvod={zdedenaPole[0][1]} /></span>
                                                )}
                                                {vlastniPole.length > 0 && (
                                                    <span className="flex items-center gap-1">
                                                        {vlastniPole.map(k => POPIS_POLE[k]).join(', ')}
                                                        <Puvod puvod={{ typ, id: Number(id) }}
                                                            onVratit={() => ulozit(vlastniPole.map(k => z(k, null, p.id)))} />
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {s.polozky.length === 0 && <p className="text-[11px] py-2" style={{ color: muted }}>{combo ? 'Skupina nemá žádné aktivní položky — přidáš je ve správě combo skupin.' : 'Skupina nemá žádné aktivní ingredience.'}</p>}
                        </div>
                        )}
                    </div>
                );
            })}

            {pridatOtevreno && (
                <Okno onZavrit={() => setPridatOtevreno(false)} isDark={isDark} sirka={400} titulek={combo ? 'Přidat combo' : 'Přidat skupinu ingrediencí'}>
                    {combo && (
                        <button type="button" onClick={() => { setPridatOtevreno(false); setEditorCombo(null); }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left"
                            style={{ background: `${BARVA}0f`, border: 'none', borderBottom: `1px solid ${border}`, cursor: 'pointer' }}>
                            <Plus size={16} style={{ color: BARVA, flexShrink: 0 }} />
                            <span>
                                <span className="block text-xs font-semibold" style={{ color: BARVA }}>Nová combo skupina</span>
                                <span className="block text-[10px]" style={{ color: muted }}>Založíš ji, přidáš položky a ceny — a rovnou se přiřadí sem.</span>
                            </span>
                        </button>
                    )}
                    {(() => {
                        const nabidka = (data?.dostupne_skupiny || []).filter(d => !skupiny.some(s => s.skupina_id === d.id && s.prirazena));
                        if (!nabidka.length) return <p className="text-xs text-center py-6" style={{ color: muted }}>{combo ? 'Všechna existující comba už jsou přiřazená.' : 'Všechny skupiny už jsou přiřazené.'}</p>;
                        return nabidka.map(d => (
                            <button key={d.id} type="button"
                                onClick={() => { setPridatOtevreno(false); ulozit([{ skupina_id: d.id, pridavek_id: null, pole: 'prirazena', hodnota: true }]); }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left"
                                style={{ background: 'none', border: 'none', borderBottom: `1px solid ${border}`, cursor: 'pointer' }}>
                                <PlusCircle size={16} style={{ color: BARVA, flexShrink: 0 }} />
                                <span>
                                    <span className="block text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{d.nazev}</span>
                                    <span className="block text-[10px]" style={{ color: muted }}>
                                        {combo
                                            ? `${d.typ_zobrazeni === 'auto' ? 'auto' : 'nabídka'} · ${d.polozky_count || 0} položek${d.popis ? ` · ${d.popis}` : ''}`
                                            : `${d.typ_vyberu === 'single' ? 'jedna z' : 'více'} · ${d.povinny ? 'povinná' : 'volitelná'} · ${d.pridavky_count || 0} ingrediencí`}
                                    </span>
                                </span>
                            </button>
                        ));
                    })()}
                </Okno>
            )}

            {editorCombo !== undefined && (
                <ComboSkupinaEditor skupinaId={editorCombo} isDark={isDark}
                    onZavrit={(ulozena) => {
                        const nova = editorCombo === null;
                        setEditorCombo(undefined);
                        // Nová skupina se přiřadí tam, odkud se zakládala.
                        if (nova && ulozena) ulozit([{ skupina_id: ulozena.id, pridavek_id: null, pole: 'prirazena', hodnota: true }]);
                        else nacist(true);
                    }} />
            )}

            {konflikt && (
                <Okno onZavrit={() => setKonflikt(null)} isDark={isDark} sirka={560}
                    titulek={`${konflikt.konflikty.length} ${konflikt.konflikty.length === 1 ? 'místo má' : konflikt.konflikty.length < 5 ? 'místa mají' : 'míst má'} pod ${data?.uzel?.nazev || 'kategorií'} vlastní hodnotu`}>
                    <div className="p-4 space-y-3">
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            Zaškrtnutá místa převezmou novou hodnotu z kategorie. Nezaškrtnutá si nechají svou. Jiná pole se nemění.
                        </p>
                        <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${border}` }}>
                            {konflikt.konflikty.map(m => {
                                const klic = `${m.typ}:${m.id}`;
                                const zaskrtnuto = konflikt.vybrana.has(klic);
                                return (
                                    <label key={klic} className="flex items-center gap-3 px-3 py-2.5" style={{ borderBottom: `1px solid ${border}`, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={zaskrtnuto}
                                            onChange={() => setKonflikt(k => {
                                                const vybrana = new Set(k.vybrana);
                                                if (vybrana.has(klic)) vybrana.delete(klic); else vybrana.add(klic);
                                                return { ...k, vybrana };
                                            })}
                                            style={{ width: 16, height: 16, accentColor: BARVA, cursor: 'pointer' }} />
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{m.nazev}</span>
                                            <span className="block text-[10px]" style={{ color: muted }}>
                                                {m.typ === 'kategorie' ? `podkategorie · ${m.produktu} produktů` : 'produkt'}
                                            </span>
                                        </span>
                                        <span className="text-[11px] text-right" style={{ color: VLASTNI }}>
                                            {m.hodnoty.map((h, i) => (
                                                <span key={i} className="block">
                                                    {h.pridavek ? `${h.pridavek}: ` : `${h.skupina}: `}{POPIS_POLE[h.pole]} {popisHodnoty(h.pole, h.hodnota)}
                                                </span>
                                            ))}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                        <p className="text-[10px]" style={{ color: muted }}>Každé přepsání se zapíše do logu změn.</p>
                    </div>
                    <div className="flex items-center justify-end gap-2 px-4 pb-4">
                        <button type="button" onClick={() => setKonflikt(null)} className="text-xs px-3 py-2"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted }}>Zrušit</button>
                        <button type="button" onClick={() => ulozit(konflikt.zmeny, 'zachovat')}
                            className="text-xs font-semibold px-3 py-2 rounded-lg"
                            style={{ background: 'none', border: `1px solid ${border}`, cursor: 'pointer', color: 'var(--text-primary)' }}>
                            Zachovat všechny vlastní
                        </button>
                        <button type="button"
                            onClick={() => ulozit(konflikt.zmeny, 'vyber', [...konflikt.vybrana])}
                            className="text-xs font-semibold px-3 py-2 rounded-lg"
                            style={{ background: BARVA, border: 'none', cursor: 'pointer', color: '#fff' }}>
                            Přepsat zaškrtnuté ({konflikt.vybrana.size})
                        </button>
                    </div>
                </Okno>
            )}
        </div>
    );
}

function Okno({ titulek, sirka, isDark, onZavrit, children }) {
    useEffect(() => {
        // Ve fázi zachycení a bez dalšího šíření — Escape zavře jen tohle okno,
        // ne i modal kategorie, ve kterém komponenta sedí.
        const onKey = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); onZavrit(); } };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [onZavrit]);

    const border = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
            onClick={e => { if (e.target === e.currentTarget) onZavrit(); }}>
            <div role="dialog" aria-modal="true" style={{ width: sirka, maxWidth: '95vw', maxHeight: '80vh', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                background: isDark ? 'rgba(24,22,32,0.96)' : 'rgba(255,255,255,0.98)', border: `1px solid ${border}`, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
                <div className="flex items-center justify-between gap-3 p-4" style={{ borderBottom: `1px solid ${border}` }}>
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{titulek}</h3>
                    <button type="button" onClick={onZavrit} aria-label="Zavřít"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={18} /></button>
                </div>
                <div className="overflow-y-auto">{children}</div>
            </div>
        </div>
    );
}
